import React from 'react'
import ScrollSnap from 'scroll-snap'
import isEqual from 'react-fast-compare'
import { forTabletLandscape } from '../../utils/responsive'
import VisibilityMonitor from './visibility-monitor'
import styles from './snapping-slider.module.scss'

class SnappingSlider extends React.Component {
    sliderRef = React.createRef()

    constructor(props) {
        super(props)
        this.state = {
            currentSlide: 0,
            currentRefCount: 0,
            tabPressCount: 0,
            currentPartition: null,
            currentSlideType: null,
            slideIsolated: false,
            slideThreshold: 0.4,
            snapPoints: {},
        }
    }

    static getDerivedStateFromProps(props, state) {
        const slides = SnappingSlider.computeSlides(props.slides || [])
        return {
            ...state,
            slides,
        }
    }

    static computeSlides(slides) {
        let indexOffset = 0

        return slides.map((Slide, index) => {
            const slide_obj = {
                Slide: Slide.Component || Slide,
                index: index + indexOffset,
            }

            if (Slide.indexControl) {
                indexOffset = Slide.indexControl(indexOffset)
            }

            return slide_obj
        })
    }

    componentDidMount() {
        if (typeof window != `undefined`) {
            // Keybinding setup
            document.addEventListener('keydown', this.onKeyPress)

            // Set appropriate active slide detection based on screen size
            document.addEventListener('resize', this.setSlideThreshold)
            this.setSlideThreshold()

            // Backup snap points handler
            if (!CSS.supports('scroll-snap-align: start')) {
                const element = this.sliderRef.current
                const snapConfig = {
                    scrollSnapDestination: '0% 100%',
                    scrollTimeout: 50,
                    scrollTime: 350,
                }
                const snapObject = new ScrollSnap(element, snapConfig)
                snapObject.bind()
            }
        }
    }

    setSlideThreshold = () => {
        // If on desktop then detection of active slide should be different.
        if (forTabletLandscape(window.innerWidth)) {
            this.setState({
                slideThreshold: 0.75,
            })
        } else {
            this.setState({
                slideThreshold: 0.4,
            })
        }
    }

    // Add event listners for keyboard keys
    onKeyPress = (e) => {
        switch (e.which) {
            // Tab key
            case 9:
                this.onTabPress(e)
                break

            // Spacebar
            case 32:
                e.preventDefault()
                // Click on the focused element and keep the focus
                if(document.activeElement) {
                    const el = document.activeElement
                    el.click()
                    el.focus()
                }
                break

            // Left
            case 37:
                e.preventDefault()
                this.prevSlide()
                break

            // Up
            case 38:
                e.preventDefault()
                this.prevSlide()
                break

            // Right
            case 39:
                e.preventDefault()
                this.nextSlide()
                break

            // Down
            case 40:
                e.preventDefault()
                this.nextSlide()
                break

            default:
                return // exit this handler for other keys
        }
    }

    // Polyfilled function to animate slide navigation
    animateToSlide = (slide) => {
        if (slide.current) {
            slide.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start',
            })
        }
    }

    navigateToIndex = (index) => {
        const slide = this.state.snapPoints[index]
        if (slide) {
            this.animateToSlide(slide)
        }
    }

    // Handle tab presses so there is still decent UX
    onTabPress = (e) => {
        const {
            currentSlide: currentSlideIndex,
            snapPoints,
            tabPressCount,
        } = this.state
        // Select the HTML element
        const currentSlide = snapPoints[currentSlideIndex]
        const currentSlideEl = currentSlide.current

        // Increase the tab count manually (no rerender)
        this.state.tabPressCount = this.state.tabPressCount + 1

        // Number of tab focusable items in the slide
        const nestedFocusableElements = currentSlideEl?.querySelectorAll(
            '[tabindex="0"]',
        )

        // Check if the slide has any direct focusable elemenets, i.e data-focusable-elements
        const discreteFocusableElements = currentSlideEl?.dataset.focusableElements
            ? JSON.parse(currentSlideEl.dataset?.focusableElements)
                .map(id => document.getElementById(id))
            : []

        // Check if this slide is attached to any elements that need focusing
        if (discreteFocusableElements.length) {
            e.preventDefault()
            // Select the nth element that is focusable
            const element = discreteFocusableElements[tabPressCount]
            if (element) {
                return element.focus()
            } else {
                // Reset tab focus so we can iterate over child focusables
                this.setState({ tabPressCount: 0 })
            }
        }

        // If we have focused on all elements in the slide then go to next slide
        e.preventDefault()
        if (nestedFocusableElements.length == 0 || tabPressCount == nestedFocusableElements.length) {
            this.nextSlide()
        // Focus on the next element on this slide
        } else {
            const el = nestedFocusableElements[tabPressCount]
            if (el) {
                el.focus()
            }
        }
    }

    nextSlide = () => {
        const { currentSlide: currentSlideIndex, snapPoints } = this.state
        const currentSlide = snapPoints[currentSlideIndex]
        const nextSlide = snapPoints[currentSlideIndex + 1]

        if (nextSlide) {
            this.animateToSlide(nextSlide)
            this.setState({ currentSlideIndex: currentSlideIndex + 1 })
        }

        // Reset the tab press count
        this.setState({ tabPressCount: 0 })
    }

    prevSlide = () => {
        const { currentSlide, snapPoints } = this.state
        const prevSlide = snapPoints[currentSlide - 1]

        if (prevSlide) {
            this.animateToSlide(prevSlide)
        }

        // Reset the tab press count
        this.setState({ tabPressCount: 0 })
    }

    // Keep track of the current slide (tracked via Intersection Observer)
    onVisibilityChange = (visibility, currentSlide, currentSlideType) => {
        if (visibility && this.state.currentSlide != currentSlide) {
            // Set new slide and reset tab count
            this.setState(
                { currentSlide, currentSlideType, tabPressCount: 0 },
                this.updateAnalytics,
            )
        }
    }

    updateAnalytics = () => {
        const { snapPoints, currentSlide, currentSlideType } = this.state
        const totalSlides = Object.keys(snapPoints).length - 1
        const progress = (currentSlide / totalSlides) * 100

        // Track scrolling progress as it passes each querter.
        const partitions = [0, 25, 50, 75, 100]
        const currentPartition = partitions.reduce((current, next) => {
            if (progress >= next) return next
            return current
        })

        if (currentPartition != this.state.currentPartition) {
            this.setState({ currentPartition }, () => {
                this.triggerPercentageView(currentPartition)
            })
        }

        // New section viewed, update GTM.
        this.triggerSectionView(currentSlide, currentSlideType)
    }

    triggerSectionView = (currentSlide, currentSlideType) => {
        if (typeof window != `undefined`) {
            window.dataLayer = window.dataLayer || []
            window.dataLayer.push({
                sectionIndex: currentSlide,
                sectionType: currentSlideType,
                event: 'sectionViewed',
            })
        }
    }

    triggerPercentageView = (currentPartition) => {
        if (typeof window != `undefined`) {
            window.dataLayer = window.dataLayer || []
            window.dataLayer.push({
                percentageScrolled: `${currentPartition}%`,
                event: 'scrollEvent',
            })
        }
    }

    setSlideIsolated = (isIsolated) =>
        this.setState({ slideIsolated: isIsolated })

    // Function passed down to slide components so they can add themselves to the slide controls.
    createSnapPoint = (index, ref) => {
        // Manual state update to stop React's setState async batching...
        if (!this.state.snapPoints && ref?.current) {
            return null
        }

        this.state.snapPoints = {
            ...this.state.snapPoints,
            [index]: ref,
        }
    }

    renderSlide = ({ Slide, Container, index }) => {
        return (
            <Slide
                key={`slide-${index}`}
                index={index}
                parentRef={this.sliderRef}
                containerClassName={styles.snappingSliderSlide}
                createSnapPoint={this.createSnapPoint}
                onVisibilityChange={this.onVisibilityChange}
                currentSlide={this.state.currentSlide}
                setIsolated={this.setSlideIsolated}
                slideThreshold={this.state.slideThreshold}
            />
        )
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isEqual(nextProps, nextState)
    }

    render() {
        const {
            containerStyle,
            slideStyle,
            scrollOnMobile = true,
            toolbars,
            Container,
        } = this.props
        const { currentSlide, slides, snapPoints, sliderLocked } = this.state

        return (
            <>
                {toolbars(
                    Object.keys(snapPoints)
                        .sort((a, b) => a - b)
                        .map((key) => snapPoints[key]),
                    this.state.currentSlide,
                )}
                <div
                    ref={this.sliderRef}
                    role="main"
                    className={[
                        !scrollOnMobile
                            ? styles.snappingSliderDesktopOnly
                            : styles.snappingSlider,
                        containerStyle,
                    ].join(' ')}
                >
                    {slides.map(this.renderSlide)}
                </div>
            </>
        )
    }
}

export default SnappingSlider
